import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Theme, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { useSetRecoilState } from 'recoil';

import { sectionUsageType } from '../../../API';
import { SectionDialogState } from '../../../atom/DialogState';

/* 1セルの高さ定義 */
const itemHeight = 60;

type SectionListType = {
  sectionId: string;
  sectionName: string;
};

const ScheduleTimeData = ({
  usageList,
  sectionList,
  section
}: {
  usageList: Array<sectionUsageType>;
  sectionList: Array<SectionListType>;
  section: string;
}) => {
  let height = 0;
  const setSectionDialogState = useSetRecoilState(SectionDialogState);
  const [searchParams] = useSearchParams();

  const title = searchParams.get('title') ?? '';
  const facilityId = searchParams.get('facilityId') ?? '';
  const areaId = searchParams.get('areaId') ?? '';
  const date = searchParams.get('date') ?? '';

  const navigate = useNavigate();

  /* サイズチェック */
  const isMobileSize = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const onLoginClick = (startTime: string, endTime: string, sectionsList: string[]) => {
    // 区画リストをカンマで結合してパラメータにする
    const sections = sectionsList.join(',');
    // TODO:遷移先を設定する。
    // ログインページを表示する。その際に、ログイン後のページを表示できるようにpathnameを渡す。
    navigate(
      `/c003/login?facilityId=${facilityId}&areaId=${areaId}&date=${date}&title=${title}&startTime=${startTime}&endTime=${endTime}&sectionList=${sections}`,
      {
        state: {
          nextPathName: `/c005/purposeregister?facilityId=${facilityId}&areaId=${areaId}&date=${date}&title=${title}&startTime=${startTime}&endTime=${endTime}&sectionList=${sections}`
        },
        replace: true
      }
    );
  };

  const onGuestClick = (startTime: string, endTime: string, sectionsList: string[]) => {
    // 区画リストをカンマで結合してパラメータにする
    const sections = sectionsList.join(',');
    // TODO:遷移先を設定する。
    // ログインページを表示する。その際に、ログイン後のページを表示できるようにpathnameを渡す。
    navigate(
      `/c005/purposeregister?facilityId=${facilityId}&areaId=${areaId}&date=${date}&title=${title}&startTime=${startTime}&endTime=${endTime}&sectionList=${sections}`
    );
  };

  // 文字サイズ判定
  // 文字数、区画数で文字サイズを調整する
  const checkFontSize = (usagePurpose: string, length: number) => {
    let fontSize = '1rem';
    const checkText = usagePurpose || '';
    const size = checkText.length;
    if (size > 5 && length > 4) {
      fontSize = '0.6rem';
    }
    return fontSize;
  };

  // TODO 施設ごとに可変対応
  // フィルター用の配列を作成し、1時間単位での表示用に加工する
  const filteredUsageList = [];
  for (let i = 0; i < usageList.length; i += 1) {
    filteredUsageList.push(usageList[i]);
    if (usageList[i].reservationFlag === 0) {
      i += 1;
    }
  }
  return (
    <>
      {filteredUsageList.map((usage: any, index: any) => {
        const start = dayjs(`1970-01-01T${usage.reservedStartTime}`);
        const end = dayjs(`1970-01-01T${usage.reservedEndTime}`);
        const diff = end.diff(start, 'minute');

        // TODO 施設ごとに可変対応
        // const slot = diff / 30;
        const slot = diff / 60;
        const thisHeight = usage.reservationFlag === 0 ? itemHeight * slot * 2 : itemHeight * slot;

        // タイトル部分
        const top = isMobileSize ? 272 + height : 278 + height;

        let width;
        switch (sectionList.length) {
          case 1:
            width = isMobileSize ? `calc(100% / 1.23)` : `calc(100% / 1.21)`;
            break;
          case 2:
            width = isMobileSize ? `calc(100% / 2.5)` : `calc(100% / 2.44)`;
            break;
          case 3:
            width = isMobileSize ? `calc(100% / 3.7)` : `calc(100% / 3.65)`;
            break;
          case 4:
            width = isMobileSize ? `calc(100% / 4.9)` : `calc(100% / 4.85)`;
            break;
          case 5:
            width = isMobileSize ? `calc(100% / 6.2)` : `calc(100% / 6.09)`;
            break;
          case 6:
            width = isMobileSize ? `calc(100% / 7.37)` : `calc(100% / 7.3)`;
            break;
          case 7:
            width = isMobileSize ? `calc(100% / 8.65)` : `calc(100% / 8.52)`;
            break;
          case 8:
            width = isMobileSize ? `calc(100% / 10.1)` : `calc(100% / 9.7)`;
            break;
          default:
            break;
        }

        let style = {};
        let text = '';

        // 予約区分に応じてスタイルを変更する。デフォルトは空き
        switch (usage.reservationFlag) {
          case 1: // 団体
            // TODO スタイル自体は上で別途定義して、ここでは設定するだけにする
            // TODO フォントサイズはテーマのサイズの何％を指定できるようにする
            style = {
              background: '#f7e0b6',
              color: 'grey',
              fontSize: checkFontSize(usage.usagePurpose, sectionList?.length),
              padding: '5px'
            };
            text = usage.usagePurpose;
            break;
          case 2: // 休館日
            style = {
              background: '#EEF1F4',
              color: 'grey',
              fontSize: '40px',
              writingMode: 'vertical-rl',
              textOrientation: 'upright',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            };
            text = '休館日';
            break;
          case 3: // 受付開始前
            style = {
              background: '#EEF1F4',
              color: 'grey',
              fontSize: '40px',
              writingMode: 'vertical-rl',
              textOrientation: 'upright',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            };
            text = '受付開始前';
            break;
          case 4: // フリー入場
            style = {
              background: '#F0825A',
              color: 'white',
              fontSize: '25px',
              writingMode: 'vertical-rl',
              textOrientation: 'upright',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            };
            text = usage.usagePurpose;
            break;
          case 5: // 受付開始前
            style = {
              background: '#EEF1F4',
              color: 'grey',
              fontSize: '35px',
              writingMode: 'vertical-rl',
              textOrientation: 'upright',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            };
            text = '受付終了';
            break;
          default: // 空いている状態
            style = {
              background: 'transparent',
              color: '#02ADA9',
              fontSize: '30px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              border: 'none'
            };
            text = '○';
            break;
        }
        height += thisHeight;

        return (
          <Button
            key={`${facilityId}${areaId}${usage.reservedStartTime}`}
            disabled={text !== '○'}
            style={{
              height: `${thisHeight}px`,
              textTransform: 'none',
              width,
              minWidth: '10px',
              top: `${top}px`,
              position: 'absolute',
              ...style
            }}
            onClick={() => {
              setSectionDialogState({
                open: true,
                sectionData: {
                  title,
                  sectionList
                },
                defSection: sectionList
                  .filter((item: { sectionId: string; sectionName: string }) => item.sectionName === section)
                  .map((item: { sectionId: string; sectionName: string }) => item.sectionId)[0], // sectionIdを取得（該当するものが複数ある場合は最初のものを取得
                defStartTime: start,
                defEndTime: end.add(30, 'minute'),
                date,
                availableStartTime: dayjs(`1970-01-01T${usageList[0].reservedStartTime}`),
                availableEndTime: dayjs(`1970-01-01T${usageList[usageList.length - 1].reservedEndTime}`),
                onGuestClick,
                onLoginClick
              });
            }}
          >
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {text}
            </div>
          </Button>
        );
      })}
    </>
  );
};

export default ScheduleTimeData;
